import Button from '../buttons/button'
import ContentTile from '../content/ContentTile'
import Counter from '../badgesAndTags/counter'
import Timeline from '../timeline/timeline'
import {
	TypographyCaps,
	TypographyDisplay,
	TypographyHeading,
	TypographyLabel,
	TypographyParagraph,
} from '../typography'
import Callout from '../callout/callout'
import { CaretDown, IPFSIcon, SignIcon, WalletIcon, XIcon } from '../icons'
import {
	bytesToMB,
	chainIDMapping,
	getMetadataProfileURL,
	getNFTCollectionInfo,
	getNFTProfileURL,
	getPageCount,
	getShareAttestationToXURL,
	isMatchingUserAddressForCIDAddress,
	truncateStringInMiddle,
} from '#app/utils/misc.tsx'
import Dropdown from './dropdown.tsx'
import { useCallback, useEffect, useState } from 'react'
import CommentForm from './signForms/commentForm.tsx'
import CastForm from './signForms/castForm.tsx'
import TweetForm from './signForms/tweetForm.tsx'
import InstagramForm from './signForms/instagramForm.tsx'
import OffchainSaleForm from './signForms/offchainSaleForm.tsx'
import ProofOfExhibitionForm from './signForms/proofOfExhibitionForm.tsx'
import MediaCitationForm from './signForms/mediaCitationForm.tsx'
import {
	AttestationType,
	MetadataProperties,
	nft,
	PinnedMetadataProperties,
	TargetWallet,
} from '#types/loreTypes.tsx'
import {
	generateMessageToSign,
	generateRollupMessageToSign,
} from '#app/utils/mintUtils.tsx'
import { Link } from '@remix-run/react'
import {
	useDynamicContext,
	useIsLoggedIn,
	DynamicUserProfile,
	useRefreshUser,
	useSwitchWallet,
	useUserWallets,
} from '@dynamic-labs/sdk-react-core'
import VideoPlaceholder from '#images/video-placeholder.jpg'
import * as templates from '#app/utils/templates/index.tsx'
import RevokeForm from './signForms/RevokeForm.tsx'
import CountersignForm from './signForms/CountersignForm.tsx'
import RollupForm from './signForms/rollupForm.tsx'
import MediaForm from './signForms/mediaForm.tsx'
import useAnalytics from '#app/utils/analytics/useAnalytics'
import InternalLink from './internalLink.tsx'
import FallbackAvatar from '#images/avatar11white.png'
import walletIcon from '#images/wallet-icon.png'
import walletIconDark from '#images/wallet-icon-dark.png'
import { useTheme } from '#app/utils/providers/themeProvider'
import { UserType } from '#app/components/inputs/userAccountLookupInput'
import {
	CastType,
	InstagramType,
} from '#app/utils/templates/utils/templateTypes.ts'

const templateOptions = Object.values(templates)
	.sort((a, b) => a.name.localeCompare(b.name))
	.filter(template => template.icon)
	.filter(template => template.slug !== 'collection')

interface PreviewPinData extends PinnedMetadataProperties {
	userAvatar: string
	username: string
}

export default function SignForm({
	nft,
	collection,
	origin,
	atomicLoreUrl,
	setPendingPin,
	setPendingSign,
	setSignedEntry,
	setConfirmationModal,
	template,
	matchingAddress,
	isValidSign,
	pendingPin,
	pendingSign,
	countersignEntry,
	showWallet,
	targetWallet,
}: {
	nft?: nft | nft[]
	collection?: AttestationType
	origin: string
	atomicLoreUrl: string
	setPendingPin?: (pendingPinEntry: MetadataProperties) => void
	setPendingSign?: (pendingSignEntry: PinnedMetadataProperties) => void
	setSignedEntry?: (signedEntry: AttestationType) => void
	setConfirmationModal?: (
		confirmationModal: {
			title: string
			message: string
			acceptButton: string
			cancelButton: string | null
		} | null,
	) => void
	template?: string
	matchingAddress?: boolean
	isValidSign?: boolean
	pendingPin?: {
		template: any
		formData?: MetadataProperties
		rollupCID?: string
		bulkCID?: string
	}
	pendingSign?: PinnedMetadataProperties
	countersignEntry?: AttestationType
	showWallet?: boolean
	targetWallet?: TargetWallet | null
}) {
	const {
		primaryWallet,
		user,
		showAuthFlow,
		setShowAuthFlow,
		setShowDynamicUserProfile,
	} = useDynamicContext()
	const userWallets = useUserWallets()
	const switchWallet = useSwitchWallet()
	const isAuthenticated = useIsLoggedIn()
	const refresh = useRefreshUser()
	const [theme] = useTheme()

	const [selectedForm, setSelectedForm] = useState({
		name: 'Select a template',
		icon: null,
		slug: '',
		version: '',
	})
	const [performingLogin, setPerformingLogin] = useState(false)
	const [missingRequiredFields, setMissingRequiredFields] = useState([])
	const [pinning, setPinning] = useState(false)
	const [formData, setFormData] = useState<MetadataProperties | {}>({})
	const [pinData, setPinData] = useState<PinnedMetadataProperties | null>(null)
	const [signData, setSignData] = useState<AttestationType | null>(null)
	const [signingMessage, setSigningMessage] = useState('')
	const [signature, setSignature] = useState('')
	const [errorMessage, setErrorMessage] = useState('')
	const [pinPreview, setPinPreview] = useState<PreviewPinData[] | null>(null)
	const [mediaFile, setMediaFile] = useState<File | undefined>(undefined)
	const [isUploading, setIsUploading] = useState<boolean>(false)
	const [selectedArtist, setSelectedArtist] = useState<UserType | undefined>(
		undefined,
	)
	const { trackEvent } = useAnalytics()

	useEffect(() => {
		if (
			template &&
			template !== selectedForm.slug &&
			selectedForm.slug.length == 0
		) {
			const newSelectedForm = templateOptions.find(
				templateOption => templateOption.slug === template,
			)
			//@ts-ignore
			newSelectedForm && setSelectedForm(newSelectedForm)
			if (
				countersignEntry &&
				countersignEntry.references &&
				countersignEntry.references.metadataIPFSCID &&
				countersignEntry.references.attestationIPFSCID
			) {
				const fieldData = {
					description: '',
					originalMetadataIPFSCID: countersignEntry.references.metadataIPFSCID,
					originalAttestationIPFSCID:
						countersignEntry.references.attestationIPFSCID,
				}
				setFormData(fieldData)
			}
		}

		if (
			pendingPin &&
			pendingPin.template &&
			Object.keys(formData).length == 0
		) {
			const newSelectedForm = templateOptions.find(
				templateOption => templateOption.slug === pendingPin.template.slug,
			)
			if (newSelectedForm) {
				//@ts-ignore
				setSelectedForm(newSelectedForm)
			}
			if (pendingPin.rollupCID) {
				setFormData({ rollupCID: pendingPin.rollupCID })
			} else if (pendingPin.formData) {
				setFormData(pendingPin.formData)
			}
		}
		if (pendingSign) {
			const newSelectedForm = templateOptions.find(
				templateOption => templateOption.slug === pendingSign.template.slug,
			)
			if (newSelectedForm) {
				//@ts-ignore
				setSelectedForm(newSelectedForm)
				mapDraftData(pendingSign)
			}
		}
	}, [])

	useEffect(() => {
		if (isAuthenticated && !primaryWallet && userWallets.length > 0) {
			const newWallet = userWallets[0]
			if (newWallet && newWallet.id) {
				switchWallet(newWallet.id)
			}
		}
	}, [isAuthenticated, primaryWallet, switchWallet, userWallets])

	useEffect(() => {
		if (isAuthenticated && !primaryWallet) {
			if (userWallets.length === 0) {
				refresh()
			} else {
				const newWallet = userWallets.find(wall => wall?.address)
				if (newWallet) switchWallet(newWallet.id)
			}
		}
		if (isAuthenticated && !showAuthFlow && performingLogin) {
			setPerformingLogin(false)
		}
		if (isAuthenticated && !validateAddressForRollup()) {
			setErrorMessage(
				' The wallet connected does not match the Signer address from the CSV imported. Open your wallet provider to connect with a different wallet.',
			)
		}
	}, [isAuthenticated, primaryWallet])

	useEffect(() => {
		if (pendingPin && pendingPin.formData) {
			setFormData(pendingPin.formData)
		}
	}, [pendingPin])

	const isUserAuthenticated = () => {
		return (
			isAuthenticated &&
			primaryWallet != null &&
			primaryWallet.address &&
			user != null
		)
	}

	const validateAddressForRollup = () => {
		// TODO support validation for Solana addresses
		if (
			pendingPin &&
			pendingPin.rollupCID &&
			pendingPin.address &&
			primaryWallet &&
			primaryWallet.address &&
			pendingPin.address.toLowerCase() !== primaryWallet?.address.toLowerCase()
		) {
			return false
		}
		return true
	}

	const validateTemplateIsCountersignable = () => {
		if (!user || !countersignEntry) return false
		if (selectedForm.slug == 'countersign') {
			return templateOptions
				.filter(template => template.countersign)
				.map(template => template.slug)
				.includes(countersignEntry.template.slug)
		} else {
			return false
		}
	}

	const validateUserCanCountersign = () => {
		if (!user || !countersignEntry) return false
		if (selectedForm.slug == 'countersign') {
			// @ts-ignore
			const counterSigningAddress =
				countersignEntry.properties.artistInitiated ||
				countersignEntry.properties.artistInitiated === undefined
					? countersignEntry.properties.receiverAddress
					: countersignEntry.properties.ownerAddress
			return isMatchingUserAddressForCIDAddress(
				counterSigningAddress,
				user.verifiedCredentials,
			)
		} else {
			return false
		}
	}

	const validateUserCanRevoke = () => {
		if (!user || !countersignEntry) return false
		if (selectedForm.slug == 'revoke') {
			// @ts-ignore
			return isMatchingUserAddressForCIDAddress(
				countersignEntry.address,
				user.verifiedCredentials,
			)
		} else {
			return false
		}
	}

	const isDataPinned = () => {
		//@ts-ignore
		return pinData && pinData.references.metadataIPFSCID
	}

	const getPreview = (passedPinData: PinnedMetadataProperties) => {
		const userAvatar =
			user && user.metadata && user.metadata.avatar
				? user.metadata.avatar
				: FallbackAvatar
		const username =
			user && user.username
				? user.username
				: pinData && pinData.address
					? pinData.address
					: ''
		const previewPinData: PreviewPinData = {
			...passedPinData,
			userAvatar,
			username,
		}
		const newPinPreview =
			previewPinData?.template.slug != 'rollup' || !pendingPin
				? previewPinData
				: {
						...previewPinData,
						properties: {
							startDate: pendingPin.startDate,
							endDate: pendingPin.endDate,
							name: pendingPin.name,
							description: pendingPin.description,
							location: pendingPin.location,
							url: pendingPin.url,
							eventCID: pendingPin.eventCID,
							rollupCIDs: pendingPin.rollupCIDs,
							rollupCID: pendingPin.rollupCID,
						},
					}
		setPinPreview([newPinPreview])
		return [newPinPreview]
	}

	const handleLogin = () => {
		setPerformingLogin(true)
		setShowAuthFlow(true)
	}

	const handleFormDataChange = (newVal: MetadataProperties | {}) => {
		if (setConfirmationModal)
			setConfirmationModal({
				title: 'Close without saving?',
				message:
					"To avoid losing the information you've entered, click on Back to Edit and then, click Continue. You can then close the form from the next step and your information will be securely stored.",
				acceptButton: 'Confirm and Close',
				cancelButton: 'Back to Edit',
			})
		setFormData(newVal)
		validateRequiredFields(false, newVal)

		if (setPendingPin)
			setPendingPin({ template: selectedForm, formData: newVal })
	}

	const resetForm = (selected: {
		name: string
		icon: any
		slug: string
		version: string
	}) => {
		setErrorMessage('')
		setMissingRequiredFields([])
		if (
			!(
				pendingPin &&
				pendingPin.template &&
				(pendingPin.template.slug === selected.slug || selected.slug === '')
			)
		) {
			setFormData({})
		}
	}

	const handleSubmit = async () => {
		// PIN payload
		// add pinned ipfs and arweave values to form entries
		if (selectedForm.slug === 'media') {
			handleMediaSubmit()
		} else if (selectedForm.slug === 'cast') {
			handleCastSubmit()
		} else if (selectedForm.slug === 'instagram') {
			handleInstagramSubmit()
		} else {
			mapPinData(formData)
		}
	}

	const handleMediaSubmit = async () => {
		if (!mediaFile) {
			return
		}
		try {
			const newFormData = { ...formData }
			const fileTypePaths = mediaFile?.type.split('/')

			// Add file specific
			// audio | file | image | video
			if (mediaFile.type === 'application/pdf') {
				newFormData.fileType = 'pdf'
			} else {
				newFormData.fileType = fileTypePaths[0]
			}

			newFormData.fileExtension = fileTypePaths[1]

			newFormData.fileName = mediaFile.name

			try {
				const calculatedMB = bytesToMB(mediaFile.size)
				if (calculatedMB === '0.0') {
					newFormData.fileSize = '< 0.1 MB'
				} else {
					newFormData.fileSize = `${calculatedMB} MB`
				}
			} catch (error) {
				console.error('ERROR GETTING PDF SIZE', error)
			}

			const mediaAttestationFormData = new FormData()

			mediaAttestationFormData.append('media', mediaFile)
			mediaAttestationFormData.append('fileType', mediaFile.type)

			const arweaveTags = {
				'App-Name': import.meta.env.VITE_APP_NAME,
				Type: 'Media',
				Topic: 'Attestations',
				'Content-Type': mediaFile.type,
				'AF-Template': selectedForm.name,
				'AF-Template-Version': selectedForm.version,
			}

			mediaAttestationFormData.append('tags', JSON.stringify(arweaveTags))

			try {
				const imageUploadResponse = await fetch('/attestationMediaUploadAPI', {
					method: 'POST',
					body: mediaAttestationFormData,
				})
				const imageUploadResponseJson = await imageUploadResponse.json()
				if (imageUploadResponseJson) {
					newFormData.arweaveTxId = imageUploadResponseJson.arweaveTxId
					newFormData.web2URL = imageUploadResponseJson.web2URL
					// if pdf
					// get pdf page count
					if (mediaFile.type === 'application/pdf') {
						try {
							const pageCount = await getPageCount(mediaFile)
							newFormData.pageCount = pageCount
						} catch (error) {
							console.error('ERROR GETTING PDF SIZE', error)
						}
					}
					mapPinData(newFormData)
				}
			} catch (error) {
				console.error('ERROR STORING Media UPLOAD', error)
			}

			// POST API
			// arweaveTxId
			// web2URL
		} catch (e) {
			console.error('Error submitting media', e)
		}
	}

	const handleCastSubmit = async () => {
		try {
			const tweetImportResponse = await fetch('/importCastAPI', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					castUrl: (formData as CastType).url,
				}),
			})

			const jsonResponse = (await tweetImportResponse.json()) as {
				success: boolean
				ipfsUrl: string
				arweaveUrl: string
				timestamp: string
			}
			if (jsonResponse.success) {
				let newData = { ...formData }
				;(newData as CastType).ipfsCID = jsonResponse.ipfsUrl
				;(newData as CastType).arweaveTxId = jsonResponse.arweaveUrl
				;(newData as CastType).timestamp = jsonResponse.timestamp
				mapPinData(newData)
			} else {
				setErrorMessage(
					'An error has occurred when attempting to retrieve the farcast. Please confirm that the URL is properly formed, and that it is accessible.',
				)
				setPinning(false)
			}
		} catch (e) {
			setErrorMessage(
				'An unexpected error has occurred when attempting to retrieve the farcast. Please try again or contact support.',
			)
			setPinning(false)
		}
	}

	const handleInstagramSubmit = async () => {
		try {
			const instagramImportResponse = await fetch('/importInstagramAPI', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					instagramUrl: (formData as InstagramType).url,
				}),
			})

			const jsonResponse = (await instagramImportResponse.json()) as {
				success: boolean
				ipfsUrl: string
				arweaveUrl: string
				timestamp: string
			}

			if (jsonResponse.success) {
				let newData = { ...formData }
				;(newData as InstagramType).ipfsCID = jsonResponse.ipfsUrl
				;(newData as InstagramType).arweaveTxId = jsonResponse.arweaveUrl
				;(newData as InstagramType).timestamp = jsonResponse.timestamp
				mapPinData(newData)
			} else {
				setErrorMessage(
					'An error has occurred when attempting to retrieve the Instagram post. Please confirm that the URL is properly formed, it belongs to a public account and that it is not flagged for sensitive content. You can still continue but we cant provide a pinned screenshot of the post for you',
				)
				setPinning(false)
			}
		} catch (e) {
			setErrorMessage(
				'An unexpected error has occurred when attempting to retrieve the Instagram post. Please try again or contact support.',
			)
			setPinning(false)
		}
	}

	const getRequiredFields = (templateSlug: string) => {
		const template = templateOptions.find(
			template => template.slug == templateSlug,
		)
		if (!template) return []
		return template.fields.filter(
			//@ts-ignore
			field => field.required === true && !field.hidden,
		)
	}

	const validateRequiredFields = (
		isSubmit: boolean,
		passedFormData?: MetadataProperties,
	) => {
		let validatingFormData = passedFormData || formData
		//@ts-ignore
		let requiredFields = getRequiredFields(selectedForm.slug)

		if (
			selectedForm.slug == 'proofOfExhibition' &&
			validatingFormData.artistInitiated &&
			!validatingFormData.countersignRequired
		)
			requiredFields = requiredFields.filter(
				field => field.slug !== 'receiverAddress',
			)
		if (
			selectedForm.slug == 'proofOfExhibition' &&
			!validatingFormData.artistInitiated &&
			!validatingFormData.countersignRequired
		)
			requiredFields = requiredFields.filter(
				field => field.slug !== 'ownerAddress',
			)

		const missingFields = requiredFields
			.filter(
				requiredField =>
					isSubmit || validatingFormData.hasOwnProperty(requiredField.slug),
			)
			.filter(
				//@ts-ignore
				requiredField =>
					//@ts-ignore
					!(validatingFormData as MetadataProperties)[requiredField.slug] ||
					//@ts-ignore
					!(validatingFormData as MetadataProperties)[requiredField.slug]
						.length,
			)

		if (!mediaFile && selectedForm.slug === 'media') {
			missingFields.push({
				name: 'media',
				slug: 'media',
				required: true,
				type: 'string',
				placeholder: '',
			})
		}

		// @ts-ignore
		const urlField = requiredFields.find(field => field.slug === 'url')

		// Todo: create explicit validation function
		if (urlField && validatingFormData.hasOwnProperty('url')) {
			// @ts-ginore
			const formUrl = validatingFormData.url
			if (!URL.canParse(formUrl)) {
				missingFields.push(urlField)
				setErrorMessage('Please fill in a valid URL')
				setPinning(false)
			} else {
				const formURLHost = new URL(formUrl).host
				if (selectedForm.slug === 'tweet') {
					const twitterDomains = ['x.com', 'twitter.com']
					if (!twitterDomains.includes(formURLHost)) {
						missingFields.push(urlField)
						setErrorMessage('Please fill in a valid Twitter URL')
						setPinning(false)
					}
				} else if (selectedForm.slug === 'instagram') {
					const instagramDomains = ['www.instagram.com', 'instagram.com']
					if (!instagramDomains.includes(formURLHost)) {
						missingFields.push(urlField)
						setErrorMessage('Please fill in a valid Instagram URL')
						setPinning(false)
					}
				} else if (selectedForm.slug === 'cast') {
					const farcastDomains = ['warpcast.com', 'www.warpcast.com']
					if (!farcastDomains.includes(formURLHost)) {
						missingFields.push(urlField)
						setErrorMessage('Please fill in a valid Warpcast URL')
						setPinning(false)
					}
				}
			}
		} else if (urlField) {
			missingFields.push(urlField)
			setErrorMessage('Please fill in a valid Warpcast URL')
			setPinning(false)
		}

		setMissingRequiredFields(missingFields)
		const isUserAuthed = isUserAuthenticated()
		if (isUserAuthed) {
			if (!missingFields.length && Object.keys(validatingFormData).length) {
				setErrorMessage('')
			} else if (missingFields.length) {
				setErrorMessage('Please fill in all the required fields.')
				setPinning(false)
			}
		} else {
			return false
		}
		return missingFields.length === 0
	}

	const mapDraftData = (data: PinnedMetadataProperties) => {
		//@ts-ignore
		if (data.references.entry) {
			setPinData(data)
			setPinPreview(getPreview(data))
			return
		}
		let draftData: PinnedMetadataProperties = {
			...data,
			references: {
				//@ts-ignore
				entry: { ...data.references },
				metadataIPFSCID: data.metadataIPFSCID,
				metadataArweaveTxId: data.arweaveTxId,
			},
		}
		delete draftData.arweaveTxId
		//@ts-ignore
		delete draftData.metadataIPFSCID
		delete draftData.isSigned
		delete draftData.nftCollection
		delete draftData.nftName
		delete draftData.nftImage
		setPinData(draftData)
		setPinPreview(getPreview(draftData))
	}

	const mapPinData = (data: MetadataProperties | {}) => {
		// Check if user is authenticated
		if (!isUserAuthenticated()) {
			setErrorMessage('You must sign in before continuing.')
			return
		}

		if (selectedForm.slug === 'revoke' || selectedForm.slug === 'countersign') {
			if (countersignEntry && countersignEntry.references) {
				//@ts-ignore
				data.originalMetadataIPFSCID =
					countersignEntry.references.metadataIPFSCID
				//@ts-ignore
				data.originalAttestationIPFSCID =
					countersignEntry.references.attestationIPFSCID
			}
		}

		const getReferencesEntry = () => {
			if (
				selectedForm.slug === 'rollup' ||
				(selectedForm.slug === 'revoke' &&
					countersignEntry &&
					countersignEntry?.template.slug === 'rollup')
			) {
				return { type: 'Rollup' }
			} else if (
				selectedForm.slug === 'bulk' ||
				(selectedForm.slug === 'revoke' &&
					countersignEntry &&
					countersignEntry?.template.slug === 'bulk')
			) {
				return { type: 'Bulk' }
			} else if (
				selectedForm.slug === 'revoke' &&
				countersignEntry &&
				countersignEntry?.template.slug === 'collection'
			) {
				//@ts-ignore
				return {
					type: 'Attestation',
					collection: countersignEntry.references.attestationIPFSCID,
				}
			} else if (collection !== undefined) {
				return {
					type: 'Attestation',
					collection: collection.references.attestationIPFSCID,
				}
			} else if (nft && nft.chain && nft.contract && nft.tokenId) {
				return {
					chain: {
						name: nft.chain.toLowerCase(),
						id: nft.chain ? chainIDMapping[nft.chain] : 'ETH',
					},
					nft: {
						contractAddress: nft.contract.address,
						tokenId: nft.tokenId,
					},
					type: 'Attestation',
				}
			} else if (targetWallet) {
				return {
					type: 'Attestation',
					targetWallet: targetWallet,
				}
			} else {
				return {}
			}
		}
		const referenceEntry = getReferencesEntry()

		let pinData: PinnedMetadataProperties = {
			//@ts-ignore
			createdAt: new Date().toISOString(),
			address: primaryWallet ? primaryWallet.address : '',
			// TODO: store and retrieve afAddress from process.env or server
			...(selectedForm.slug === 'rollup' && {
				afAddress: '0x00AF',
			}),
			template: {
				name: selectedForm.name,
				slug: selectedForm.slug,
				version: selectedForm.version,
			},
			references: {
				entry: referenceEntry,
			},
			properties: {
				...data,
				...(data.hasOwnProperty('countersignRequired') && {
					// @ts-ignore
					countersignStatus:
						data.countersignRequired === true ? 'Pending' : 'Completed',
				}),
			} as MetadataProperties,
			userId: user.userId,
			...(countersignEntry &&
				countersignEntry.properties && {
					originalReference: {
						metadataIPFSCID: countersignEntry.references.metadataIPFSCID,
						...(countersignEntry.references.attestationIPFSCID && {
							attestationIPFSCID:
								countersignEntry.references.attestationIPFSCID,
						}),
						...(countersignEntry.message && {
							message: countersignEntry.message,
						}),
						address: countersignEntry.address,
						...(countersignEntry.signature && {
							signature: countersignEntry.signature,
						}),
						//@ts-ignore
						uid: countersignEntry.uid,
						...(countersignEntry.createdAt && {
							createdAt: countersignEntry.createdAt,
						}),
						//@ts-ignore
						...(countersignEntry.properties.countersignRequired && {
							//@ts-ignore
							countersignRequired:
								countersignEntry.properties.countersignRequired,
						}),
						//@ts-ignore
						...(countersignEntry.properties.countersignStatus && {
							//@ts-ignore
							...(countersignEntry.countersignStatus && {
								//@ts-ignore
								countersignStatus: countersignEntry.countersignStatus,
							}),
							//@ts-ignore
							countersigningAddress:
								countersignEntry.properties.receiverAddress,
						}),
						type: 'Single',
						template: {
							name: countersignEntry.template.name,
							slug: countersignEntry.template.slug,
							version: countersignEntry.template.version,
						},
					},
				}),
		}
		setPinData(pinData)
		if (pinData && !pinData.references.metadataIPFSCID) {
			setErrorMessage('')
			setPinPreview(getPreview(pinData))
			handlePin(pinData)
		}
	}

	const handlePin = async (pinData: any) => {
		let success = false
		let newPinData = {}
		if (!pinData) return
		try {
			const pinResponse = await fetch('/pinMetadataAPI', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ pinData }),
			})
			const jsonResponse = (await pinResponse.json()) as {
				success: boolean
				ipfsCID: string
				arweaveTxId: string
			}
			if (jsonResponse.success === true) {
				trackEvent('Client_Attestation_Pinned', {
					template: pinData.template.slug,
					metadataIPFSCID: jsonResponse.ipfsCID,
					...(pinData.references.entry &&
						pinData.references.entry.chain &&
						pinData.references.entry.chain.name && {
							chain: pinData.references.entry.chain.name,
						}),
					...(pinData.references.entry &&
						pinData.references.entry.nft &&
						pinData.references.entry.nft.contractAddress && {
							contractAddress: pinData.references.entry.nft.contractAddress,
						}),
					...(pinData.references.entry &&
						pinData.references.entry.nft &&
						pinData.references.entry.nft.tokenId && {
							tokenId: pinData.references.entry.nft.tokenId,
						}),
					...(pinData.references.entry &&
						pinData.references.collection && {
							collection: pinData.references.collection,
						}),
				})
				newPinData = {
					...pinData,
					references: {
						...pinData.references,
						...(pendingPin &&
							pendingPin.eventCID && {
								eventCID: pendingPin.eventCID,
							}),
						metadataIPFSCID: jsonResponse.ipfsCID,
						...(jsonResponse.arweaveTxId && {
							metadataArweaveTxId: jsonResponse.arweaveTxId,
						}),
					},
				}
				setPinData(newPinData)
				setPinPreview(getPreview(newPinData))
				success = true
				if (setPendingSign) setPendingSign(newPinData)
				if (setConfirmationModal)
					setConfirmationModal({
						title: "You haven't signed this entry",
						message:
							"If you close this form without signing your attestation, all the information you've entered will be securely stored in the Signatures section as a Draft in the Profile Manager.",
						acceptButton: 'Close and sign later',
						cancelButton: 'Sign now',
					})
			} else {
				setErrorMessage(
					'An error has occurred when attempting to store your entry. Please verify the provided information and try again, or contact support.',
				)
			}
		} catch (e) {
			setErrorMessage(
				'An unexpected error has occurred when attempting to store your entry. Please try again or contact support.',
			)
		} finally {
			setPinning(false)
			if (success && newPinData) {
				return newPinData
			}
		}
	}

	const handlePrepareSign = () => {
		let signableMessage: string
		if (selectedForm.slug === 'rollup') {
			signableMessage = generateRollupMessageToSign(selectedForm, pinData)
		} else {
			signableMessage = generateMessageToSign(
				nft,
				collection,
				selectedForm,
				countersignEntry,
				pinData,
			)
		}
		setSigningMessage(signableMessage)
		if (signableMessage && signableMessage.length && pinData) {
			setErrorMessage('')
			handleSign(signableMessage, pinData)
		}
	}

	const handleSign = async (
		signingMessage: string,
		signingPinData: PinnedMetadataProperties,
	) => {
		// Check if user is authenticated
		if (!isUserAuthenticated() || primaryWallet === null) {
			setErrorMessage(
				'An error has occurred when attempting to retrieve your wallet address for signing.',
			)
			return
		}

		let signature
		try {
			signature = await primaryWallet.signMessage(signingMessage)
		} catch (e) {
			if ((e as Error).message) {
				setErrorMessage(
					`An error has occurred when requesting your signature: ${(e as Error).message.split('\n')[0]}`,
				)
			} else {
				setErrorMessage(
					'An unexpected error has occurred when requesting your signature. Please try again.',
				)
			}
			setSigningMessage('')
			return
		}

		setSignature(signature)

		let payload = {
			...signingPinData,
			message: signingMessage,
			signature: signature,
			...(countersignEntry &&
				countersignEntry.properties && {
					originalReference: {
						metadataIPFSCID: countersignEntry.references.metadataIPFSCID,
						...(countersignEntry.references.attestationIPFSCID && {
							attestationIPFSCID:
								countersignEntry.references.attestationIPFSCID,
						}),
						...(countersignEntry.message && {
							message: countersignEntry.message,
						}),
						address: countersignEntry.address,
						...(countersignEntry.signature && {
							signature: countersignEntry.signature,
						}),
						//@ts-ignore
						uid: countersignEntry.uid,
						...(countersignEntry.createdAt && {
							createdAt: countersignEntry.createdAt,
						}),
						//@ts-ignore
						...(countersignEntry.properties.countersignRequired && {
							//@ts-ignore
							countersignRequired:
								countersignEntry.properties.countersignRequired,
						}),
						//@ts-ignore
						...(countersignEntry.properties.countersignStatus && {
							//@ts-ignore
							...(countersignEntry.countersignStatus && {
								//@ts-ignore
								countersignStatus: countersignEntry.countersignStatus,
							}),
							//@ts-ignore
							countersigningAddress: countersignEntry.properties.artistInitiated
								? countersignEntry.properties.receiverAddress
								: countersignEntry.properties.ownerAddress,
						}),
						//@ts-ignore
						...(countersignEntry.properties.artistInitiated === undefined &&
							countersignEntry.properties.receiverAddress && {
								//@ts-ignore
								countersigningAddress:
									countersignEntry.properties.receiverAddress,
							}),
						properties: countersignEntry.properties,
						type: 'Single',
						template: {
							name: countersignEntry.template.name,
							slug: countersignEntry.template.slug,
							version: countersignEntry.template.version,
						},
					},
				}),
		}

		//@ts-ignore
		if (
			(countersignEntry &&
				countersignEntry.originalReference &&
				countersignEntry.originalReference.uid) ||
			(countersignEntry && countersignEntry.uid)
		) {
			const origUID =
				(countersignEntry.originalReference &&
					countersignEntry.originalReference.uid) ||
				countersignEntry.uid
			//@ts-ignore
			payload.originalAttestationUID = origUID
		}

		try {
			const signResponse = await fetch('/signMetadataAPI', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ payload }),
			})
			const jsonResponse = (await signResponse.json()) as {
				success: boolean
				data: any
			}
			if (jsonResponse.success === true && jsonResponse.data) {
				trackEvent('Client_Attestation_Attested', {
					template: jsonResponse.data.template.slug,
					address: jsonResponse.data.address,
					metadataIPFSCID: jsonResponse.data.references.metadataIPFSCID,
					attestationIPFSCID: jsonResponse.data.references.attestationIPFSCID,
				})

				setSignData(jsonResponse.data)
				setFormData({})
				setPinData(null)
				setSignature('')
				setSigningMessage('')
				if (setSignedEntry) setSignedEntry(jsonResponse.data)
			} else {
				setSigningMessage('')
				setErrorMessage(
					'An error has occurred when attempting to sign your entry. Please verify the provided information and try again, or contact support.',
				)
			}
		} catch (e) {
			setSigningMessage('')
			setErrorMessage(
				'An unexpected error has occurred when attempting to sign your entry. Please try again or contact support.',
			)
		}
	}
	const getFormTitle = useCallback(
		({
			selectedForm,
			nft,
			collection,
			targetWallet,
		}: {
			selectedForm: any
			nft: any
			collection: any
			targetWallet?: TargetWallet | null
		}) => {
			if (selectedForm?.slug === 'revoke') {
				return 'Revoke attestation'
			}

			if (targetWallet) {
				return `Sign Wallet`
			}

			if (nft !== undefined && collection === undefined) {
				return Array.isArray(nft) ? 'Sign Rollup' : 'Sign NFT'
			}

			if (
				(!selectedForm ||
					(selectedForm.slug !== 'revoke' &&
						selectedForm.slug !== 'countersign')) &&
				collection !== undefined
			) {
				return 'Sign Collection'
			}

			return ''
		},
		[],
	)

	const getFormDescription = useCallback(
		({
			targetWallet,
			nft,
			collection,
		}: {
			targetWallet?: TargetWallet | null
			nft?: nft | nft[]
			collection?: AttestationType
		}) => {
			if (targetWallet) {
				return 'Add offchain content directly to your profile by signing to your wallet.'
			}
			return `Provide context, history, social proof, or any relevant information to this ${nft !== undefined ? 'token' : ''}${collection !== undefined ? 'Collection' : ''} by adding onchain data.`
		},
		[],
	)
	const getSuccessSubTitle = useCallback(
		({
			slug,
			targetWallet,
		}: {
			slug: string
			targetWallet?: TargetWallet | null
		}) => {
			if (slug === 'revoke') {
				return 'Your attestation has been revoked'
			} else if (targetWallet) {
				return 'Another piece of lore saved forever'
			}
			return `You have made art immortal 🫡`
		},
		[],
	)
	const getSuccessBody = useCallback(
		({
			slug,
			templateName,
			targetWallet,
		}: {
			slug: string
			templateName: string
			targetWallet?: TargetWallet | null
		}) => {
			if (slug === 'revoke') {
				return `Your changes will soon be reflected in your Attestations history. If the update isn't visible, try refreshing the page.`
			} else if (targetWallet) {
				return `You've officially signed history to your wallet! Your wallet's onchain lore is now visible on your user profile. If you don’t see it right away, try refreshing the page.`
			}
			return `You've successfully attested this ${templateName}. The Onchain history should update to reflect the newattestation soon. If it doesn't, please refresh the page`
		},
		[],
	)
	const getTemplateOptions = useCallback(() => {
		// We currently don't support wallet bulk or rollup attestations
		// So if there is a wallet present we can ignore the filter in the else
		if (targetWallet) {
			return templateOptions.filter(templateOption =>
				['comment', 'tweet', 'media', 'cast', 'mediaCitation'].includes(
					templateOption.slug,
				),
			)
		} else {
			return templateOptions.filter(templateOption =>
				countersignEntry
					? //@ts-ignore
						templateOption.isCountersign && templateOption.slug == template
					: //@ts-ignore
						pendingPin && pendingPin.rollupCID
						? templateOption.slug === 'rollup'
						: pendingPin && pendingPin.bulkCID
							? templateOption.slug === 'bulk'
							: !templateOption.isCountersign &&
								templateOption.slug !== 'rollup' &&
								templateOption.slug !== 'bulk',
			)
		}
	}, [templateOptions, countersignEntry, pendingPin, template])

	const handleBackToEdit = () => {
		setErrorMessage('')
		setSigningMessage('')
		setPinPreview(null)
		setPinData(null)
		if (pinData && pinData.properties) setFormData(pinData.properties)
		if (setConfirmationModal)
			setConfirmationModal({
				title: 'Your information was saved',
				message:
					"All the information you've entered will be available in the Signatures section as a Draft in the Profile Manager. You can come back and continue signing at any time.",
				acceptButton: 'Close',
				cancelButton: null,
			})
		setPinData(null)
	}

	return (
		<div className="w-full max-w-5xl">
			{(!signData || !signData.references.attestationIPFSCID) && (
				<>
					<DynamicUserProfile />
					<div className="flex flex-col justify-between md:flex-row">
						<TypographyDisplay
							size="medium"
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
						>
							{getFormTitle({
								selectedForm,
								nft,
								collection,
								targetWallet,
							})}
						</TypographyDisplay>
						{!isUserAuthenticated() && (
							<Button
								variant="secondary"
								disabled={true}
								rightIcon={<WalletIcon />}
							>
								Not connected
							</Button>
						)}
						{showWallet && isUserAuthenticated() && (
							<Button
								variant="secondary"
								onClick={() => setShowDynamicUserProfile(true)}
								rightIcon={<CaretDown />}
							>
								{truncateStringInMiddle(primaryWallet.address)}
							</Button>
						)}
					</div>
					<div className="mt-8 flex flex-col gap-x-4 gap-y-2 md:flex-row">
						<div className="flex flex-col md:w-2/5 md:pr-20 lg:pr-28 xl:pr-36">
							{Array.isArray(nft) ? (
								<>
									<div className="flex flex-row items-center gap-x-2">
										<TypographyHeading
											size="medium"
											isBold={true}
											additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
										>
											Associated NFTs
										</TypographyHeading>
										<Counter
											data-testid="rollup-imported-total"
											size="small"
											children={nft.length}
										/>
									</div>
									<TypographyParagraph
										size="medium"
										additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
									>
										NFTs associated to this onchain attestation
									</TypographyParagraph>
								</>
							) : (
								<TypographyParagraph
									size="medium"
									additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
								>
									{getFormDescription({ targetWallet, nft, collection })}
								</TypographyParagraph>
							)}
						</div>
						<div className="flex flex-col md:w-3/5">
							{!collection && nft && (
								<>
									{Array.isArray(nft) ? (
										<div className="max-h-96 overflow-y-auto">
											<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
												{nft.length > 0 &&
													nft.map((nftEntry, index) => {
														return (
															<InternalLink
																additionalClasses="flex w-full flex-row text-center"
																key={`nft-${index}`}
																to={getNFTProfileURL(nftEntry)}
																prefetch="none"
															>
																<ContentTile
																	title={nftEntry.name}
																	description={
																		getNFTCollectionInfo(nftEntry) &&
																		getNFTCollectionInfo(nftEntry).name
																			? getNFTCollectionInfo(nftEntry).name
																			: 'Unknown'
																	}
																	image={
																		nftEntry.image &&
																		typeof nftEntry.image !== 'string' &&
																		nftEntry.image.contentType &&
																		nftEntry.image.contentType.includes('video')
																			? VideoPlaceholder
																			: nftEntry.previewImage
																	}
																	isVerified={false}
																	showCaret={false}
																	showBorder={false}
																	variant="collection"
																/>
															</InternalLink>
														)
													})}
											</div>
										</div>
									) : (
										<InternalLink
											additionalClasses="flex w-full flex-row text-center"
											to={getNFTProfileURL(nft)}
											prefetch="none"
										>
											<ContentTile
												title={nft.name}
												description={
													nft.nftCollection ? nft.nftCollection.name : 'Unknown'
												}
												image={
													nft.image &&
													typeof nft.image !== 'string' &&
													nft.image.contentType &&
													nft.image.contentType.includes('video')
														? VideoPlaceholder
														: nft.previewImage
												}
												isVerified={false}
												showCaret={false}
												showBorder={true}
												variant="collection"
											/>
										</InternalLink>
									)}
								</>
							)}
							{collection && (
								<InternalLink
									additionalClasses="flex w-full flex-row text-center"
									to={`/collectionProfile/${collection.references.attestationIPFSCID}`}
									prefetch="none"
								>
									<ContentTile
										title={collection.properties.name}
										description={collection.properties.description}
										image={collection.properties.imageUrl}
										isVerified={false}
										showCaret={false}
										showBorder={false}
										variant="collection"
									/>
								</InternalLink>
							)}
							{targetWallet && (
								<ContentTile
									onClick={
										selectedForm.slug !== 'revoke'
											? () => setShowDynamicUserProfile(true)
											: undefined
									}
									title={truncateStringInMiddle(targetWallet.address)}
									description="Associated Wallet"
									image={theme === 'dark' ? walletIconDark : walletIcon}
									isVerified={false}
									showCaret={false}
									showBorder={true}
									variant="collection"
									elementRight={
										selectedForm.slug !== 'revoke' ? (
											<Button variant="tertiary" size="small">
												Switch Wallet
											</Button>
										) : null
									}
								/>
							)}
						</div>
					</div>
				</>
			)}

			{!signData && !isDataPinned() && selectedForm.slug !== 'rollup' && (
				<div className="mt-8 flex flex-col gap-x-4 gap-y-2 md:flex-row">
					<div className="flex flex-col md:w-2/5 md:pr-20 lg:pr-28 xl:pr-36">
						<TypographyHeading
							size="medium"
							isBold={true}
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
						>
							{selectedForm.slug !== 'revoke'
								? 'What do you want to sign?'
								: `Revoking ${countersignEntry?.template.name}`}
						</TypographyHeading>
						<TypographyParagraph
							size="medium"
							additionalClasses="mt-2 text-textSecondaryLight dark:text-textSecondaryDark"
						>
							{selectedForm.slug !== 'revoke'
								? 'Select one of the Lore templates'
								: `This will revoke the existing ${countersignEntry?.template.name} attestation.`}
						</TypographyParagraph>
						{selectedForm.slug === 'revoke' &&
							countersignEntry?.template.slug === 'rollup' && (
								<>
									<a
										href={`/metadataProfile/rollup/${countersignEntry.references.attestationIPFSCID}`}
										target="_blank"
									>
										<Button variant="primary" additionalClasses="mt-2 w-full">
											View Roll-up
										</Button>
									</a>
									<TypographyParagraph
										size="medium"
										additionalClasses="mt-2 text-textSecondaryLight dark:text-textSecondaryDark"
									>
										This will also revoke all derived attestations originally
										generated by the rollup. This process will be run
										asynchronously after revoking the rollup.
									</TypographyParagraph>
								</>
							)}
						{!selectedForm ||
							(selectedForm.slug !== 'revoke' &&
								selectedForm.slug !== 'countersign' && (
									<div className="flex flex-row items-center">
										<TypographyLabel
											size="small"
											additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
										>
											Need help choosing?
										</TypographyLabel>
										<a href="https://www.atomicform.com/lore" target="_blank">
											<Button
												variant="tertiary"
												additionalClasses="ml-1 text-textPrimaryLight dark:text-textPrimaryDark"
											>
												Read more
											</Button>
										</a>
									</div>
								))}{' '}
					</div>
					<div className="flex flex-col md:w-3/5">
						<Dropdown
							disabled={selectedForm.slug === 'revoke'}
							title="PROVENANCE TEMPLATES"
							placeholder="Select a template"
							selected={selectedForm}
							onSelectionChange={selected => {
								resetForm(selected)
								setSelectedForm(selected)
							}}
							options={[
								//@ts-ignore
								{
									name: 'Select a template',
									icon: null,
									slug: '',
									version: '',
								},
								//@ts-ignore
								...getTemplateOptions(),
							]}
						/>
						<div className="mt-8">
							{selectedForm.slug == 'comment' && (
								<CommentForm
									formData={formData}
									setFormData={newVal => handleFormDataChange(newVal)}
									requiredFields={getRequiredFields('comment')}
									errorFields={missingRequiredFields}
								/>
							)}
							{selectedForm.slug == 'tweet' && (
								<TweetForm
									nft={nft}
									selectedForm={selectedForm}
									formData={formData}
									setFormData={newVal => handleFormDataChange(newVal)}
									requiredFields={getRequiredFields('tweet')}
									errorFields={missingRequiredFields}
								/>
							)}
							{selectedForm.slug == 'media' && (
								<MediaForm
									nft={nft}
									selectedForm={selectedForm}
									file={mediaFile}
									setFile={setMediaFile}
									formData={formData}
									setFormData={newVal => handleFormDataChange(newVal)}
									requiredFields={getRequiredFields('media')}
									errorFields={missingRequiredFields}
								/>
							)}
							{selectedForm.slug == 'cast' && (
								<CastForm
									formData={formData}
									setFormData={newVal => handleFormDataChange(newVal)}
									requiredFields={getRequiredFields('cast')}
									errorFields={missingRequiredFields}
								/>
							)}
							{selectedForm.slug == 'instagram' && (
								<InstagramForm
									formData={formData}
									setFormData={newVal => handleFormDataChange(newVal)}
									requiredFields={getRequiredFields('instagram')}
									errorFields={missingRequiredFields}
								/>
							)}
							{selectedForm.slug == 'offchainSale' && (
								<OffchainSaleForm
									formData={formData}
									setFormData={newVal => handleFormDataChange(newVal)}
									requiredFields={getRequiredFields('offchainSale')}
									errorFields={missingRequiredFields}
								/>
							)}
							{selectedForm.slug == 'mediaCitation' && (
								<MediaCitationForm
									formData={formData}
									setFormData={newVal => handleFormDataChange(newVal)}
									requiredFields={getRequiredFields('mediaCitation')}
									errorFields={missingRequiredFields}
								/>
							)}
							{selectedForm.slug == 'proofOfExhibition' && (
								<ProofOfExhibitionForm
									formData={formData}
									setFormData={newVal => handleFormDataChange(newVal)}
									requiredFields={getRequiredFields('proofOfExhibition')}
									errorFields={missingRequiredFields}
									address={primaryWallet ? primaryWallet.address : undefined}
								/>
							)}
							{selectedForm.slug == 'revoke' &&
								countersignEntry &&
								validateUserCanRevoke() &&
								isUserAuthenticated() && (
									<>
										<Timeline
											//@ts-ignore
											metadataEntries={[countersignEntry]}
											metadataAdditionalClasses="w-full"
											atomicLoreUrl={atomicLoreUrl}
											tweetsUrl={`${atomicLoreUrl}/getTweet`}
											displayAvatars={false}
											displayEmbedContent={false}
										/>
										<RevokeForm
											formData={formData}
											setFormData={newVal => handleFormDataChange(newVal)}
											requiredFields={missingRequiredFields}
										/>
									</>
								)}
							{selectedForm.slug == 'revoke' &&
								(!validateUserCanRevoke() || !isUserAuthenticated()) && (
									<Callout
										variant="error"
										additionalClasses="w-full"
										content="You must be authenticated with the same wallet that signed the original entry in order to revoke it."
									/>
								)}
							{selectedForm.slug == 'countersign' &&
								isUserAuthenticated() &&
								countersignEntry &&
								validateTemplateIsCountersignable() &&
								!validateUserCanCountersign() && (
									<Callout
										variant="error"
										additionalClasses="w-full"
										content="You must be authenticated with the countersigning wallet provided in the original attestation in order to countersign."
									/>
								)}
							{selectedForm.slug == 'countersign' &&
								isUserAuthenticated() &&
								countersignEntry &&
								validateTemplateIsCountersignable() &&
								countersignEntry.isReattested && (
									<Callout
										variant="error"
										additionalClasses="w-full"
										content="This entry has already been countersigned."
									/>
								)}
							{selectedForm.slug == 'countersign' &&
								isUserAuthenticated() &&
								countersignEntry &&
								!validateTemplateIsCountersignable() && (
									<Callout
										variant="error"
										additionalClasses="w-full"
										content="You cannot countersign this attestation."
									/>
								)}
							{selectedForm.slug == 'countersign' &&
								countersignEntry &&
								!countersignEntry.isReattested &&
								validateTemplateIsCountersignable() &&
								isUserAuthenticated() && (
									<>
										<Timeline
											//@ts-ignore
											metadataEntries={[countersignEntry]}
											metadataAdditionalClasses="w-full"
											atomicLoreUrl={atomicLoreUrl}
											tweetsUrl={`${atomicLoreUrl}/getTweet`}
											displayAvatars={false}
											displayEmbedContent={false}
										/>
										<CountersignForm
											formData={formData}
											setFormData={newVal => handleFormDataChange(newVal)}
											requiredFields={missingRequiredFields}
										/>
									</>
								)}
							{selectedForm.slug == 'countersign' && !isUserAuthenticated() && (
								<Callout
									variant="error"
									additionalClasses="w-full"
									content="You must be authenticated to countersign the original entry."
								/>
							)}
						</div>
					</div>
				</div>
			)}

			{!signData &&
				!isDataPinned() &&
				selectedForm.slug == 'rollup' &&
				pendingPin && (
					<div className="mt-8 flex flex-col gap-x-4 gap-y-2 md:flex-row">
						<div className="flex flex-col md:w-2/5 md:pr-20 lg:pr-28 xl:pr-36">
							<TypographyHeading
								size="medium"
								isBold={true}
								additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
							>
								Attestation Details
							</TypographyHeading>
							<TypographyParagraph
								size="medium"
								additionalClasses="mt-2 text-textSecondaryLight dark:text-textSecondaryDark"
							>
								Review your onchain attestation details and tap Continue to
								initiate the signing process.
							</TypographyParagraph>
						</div>
						<div className="flex flex-col md:w-3/5">
							<Timeline
								//@ts-ignore
								metadataEntries={[
									{
										createdAt: new Date().toISOString(),
										address: pendingPin.address,
										template: {
											name: 'Proof of exhibition',
											slug: 'proofOfExhibition',
										},
										references: {
											entry: {
												nft: { contractAddress: '', tokenId: '' },
												type: 'Rollup',
											},
										},
										properties: {
											startDate: pendingPin.startDate,
											endDate: pendingPin.endDate,
											name: pendingPin.name,
											description: pendingPin.description,
											location: pendingPin.location,
											url: pendingPin.url,
											receiverAddress: pendingPin.address,
											eventCID: pendingPin.eventCID,
											rollupCIDs: pendingPin.rollupCIDs,
											rollupCID: pendingPin.rollupCID,
										},
									},
								]}
								metadataAdditionalClasses="w-full"
								atomicLoreUrl={atomicLoreUrl}
								tweetsUrl={`${atomicLoreUrl}/getTweet`}
								displayAvatars={false}
								displayEmbedContent={false}
							/>
							<RollupForm formData={{ rollupCID: pendingPin.rollupCID }} />
						</div>
					</div>
				)}

			{!signData && isDataPinned() && !signingMessage && (
				<>
					<div className="mt-8 flex flex-col gap-x-4 gap-y-2 md:flex-row">
						<div className="flex flex-col md:w-2/5 md:pr-20 lg:pr-28 xl:pr-36">
							<TypographyHeading
								size="medium"
								isBold={true}
								additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
							>
								Does everything look good?
							</TypographyHeading>
							<TypographyParagraph
								size="medium"
								additionalClasses="mt-2 text-textSecondaryLight dark:text-textSecondaryDark"
							>
								Review your onchain data before signing. After confirming, you
								will be asked to sign the message from your wallet provider (ex:
								MetaMask).
							</TypographyParagraph>
						</div>
						<div className="flex flex-col md:w-3/5">
							<Timeline
								key={
									pinPreview &&
									pinPreview.length > 0 &&
									pinPreview[0].properties &&
									pinPreview[0].properties.ipfsCID
										? pinPreview[0].properties.ipfsCID
										: 'no-key'
								}
								//@ts-ignore
								metadataEntries={pinPreview}
								metadataAdditionalClasses="w-full"
								atomicLoreUrl={atomicLoreUrl}
								tweetsUrl={`${atomicLoreUrl}/getTweet`}
								displayAvatars={true}
								displayEmbedContent={false}
								isPreview={true}
							/>
							{pinData?.template.slug == 'rollup' &&
								pinData.references.metadataIPFSCID && (
									<div className="mt-2 flex flex-row items-center pl-2 text-textPrimaryLight dark:text-textPrimaryDark">
										<IPFSIcon />
										<TypographyParagraph
											size="medium"
											additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark ml-4 mr-2"
										>
											IPFS CID:
										</TypographyParagraph>
										<TypographyLabel
											size="small"
											additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
										>
											{pinData.references.metadataIPFSCID}
										</TypographyLabel>
									</div>
								)}
						</div>
					</div>
				</>
			)}
			{isDataPinned() && signingMessage && !signature && (
				<>
					<div className="mt-8 flex flex-col gap-x-4 gap-y-2 md:flex-row">
						<div className="flex flex-col md:w-2/5 md:pr-20 lg:pr-28 xl:pr-36">
							<TypographyHeading
								size="medium"
								isBold={true}
								additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
							>
								Requesting Signature.
							</TypographyHeading>
							<TypographyParagraph
								size="medium"
								additionalClasses="mt-2 text-textSecondaryLight dark:text-textSecondaryDark"
							>
								Sign the message from your wallet provider to confirm this
								transaction.
							</TypographyParagraph>
						</div>
						<div className="flex flex-col md:w-3/5">
							<Timeline
								key={
									pinPreview &&
									pinPreview.length > 0 &&
									pinPreview[0].properties &&
									pinPreview[0].properties.ipfsCID
										? pinPreview[0].properties.ipfsCID
										: 'no-key'
								}
								//@ts-ignore
								metadataEntries={pinPreview}
								metadataAdditionalClasses="w-full"
								atomicLoreUrl={atomicLoreUrl}
								tweetsUrl={`${atomicLoreUrl}/getTweet`}
								displayAvatars={true}
								displayEmbedContent={false}
							/>
						</div>
					</div>
				</>
			)}
			{isDataPinned() && signingMessage && signature && !signData && (
				<>
					<div className="mt-8 flex flex-col gap-x-4 gap-y-2 md:flex-row">
						<div className="flex flex-col md:w-2/5 md:pr-20 lg:pr-28 xl:pr-36">
							<TypographyHeading
								size="medium"
								isBold={true}
								additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
							>
								Creating Attestation...
							</TypographyHeading>
							<TypographyParagraph
								size="medium"
								additionalClasses="mt-2 text-textSecondaryLight dark:text-textSecondaryDark"
							>
								Please don't close this window until we've made your art
								immortal.
							</TypographyParagraph>
						</div>
						<div className="flex flex-col md:w-3/5">
							<Timeline
								key={
									pinPreview &&
									pinPreview.length > 0 &&
									pinPreview[0].properties &&
									pinPreview[0].properties.ipfsCID
										? pinPreview[0].properties.ipfsCID
										: 'no-key'
								}
								//@ts-ignore
								metadataEntries={pinPreview}
								metadataAdditionalClasses="w-full"
								atomicLoreUrl={atomicLoreUrl}
								tweetsUrl={`${atomicLoreUrl}/getTweet`}
								displayAvatars={true}
								displayEmbedContent={false}
							/>
						</div>
					</div>
				</>
			)}
			{signData && signData.references.attestationIPFSCID && (
				<>
					<div className="mx-auto mt-8 flex flex-col justify-center text-center md:w-full">
						<TypographyCaps size="medium" additionalClasses="text-[#1AB51A]">
							Success!
						</TypographyCaps>
						<TypographyHeading
							size="large"
							additionalClasses="mt-4 text-textPrimaryLight dark:text-textPrimaryDark"
						>
							{getSuccessSubTitle({
								slug: signData.template.slug,
								targetWallet,
							})}
						</TypographyHeading>
						<TypographyParagraph
							size="medium"
							additionalClasses="mt-4 text-textSecondaryLight dark:text-textSecondaryDark text-left"
						>
							{getSuccessBody({
								slug: signData.template.slug,
								templateName: signData.template.name,
								targetWallet,
							})}
						</TypographyParagraph>
						{signData.references.attestationIPFSCID && (
							<div className="flex flex-col items-center justify-center">
								<InternalLink
									additionalClasses="flex flex-row justify-center text-center"
									to={getMetadataProfileURL(signData) || ''}
									prefetch="none"
								>
									<Button variant="primary" additionalClasses="mt-4">
										View Provenance
									</Button>
								</InternalLink>
								<Link
									to={getShareAttestationToXURL(
										signData,
										atomicLoreUrl,
										nft,
										targetWallet,
									)}
									target="_blank"
								>
									<Button
										variant="tertiary"
										leftIcon={<XIcon />}
										additionalClasses="mt-2"
									>
										Share on Twitter
									</Button>
								</Link>
							</div>
						)}
					</div>
				</>
			)}
			{(!signData || !signData.references.attestationIPFSCID) && (
				<div className="mt-8 flex flex-row gap-x-4">
					<div className="flex w-0 flex-col md:w-2/5">
						{/*<TypographyHeading size="small" additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark">3. Store and sign</TypographyHeading>*/}
					</div>
					<div className="flex w-full flex-col md:!w-3/5">
						{errorMessage && (
							<Callout
								variant="error"
								content={errorMessage}
								additionalClasses="w-full mb-4"
							/>
						)}
						{!isUserAuthenticated() && (
							<Button
								variant="primary"
								additionalClasses="w-full"
								onClick={handleLogin}
							>
								Connect Wallet
							</Button>
						)}
						{isUserAuthenticated() &&
							validateAddressForRollup() &&
							!isDataPinned() &&
							!signingMessage.length &&
							!signData &&
							(selectedForm.slug == 'revoke'
								? countersignEntry && validateUserCanRevoke()
								: true) &&
							(selectedForm.slug == 'countersign'
								? countersignEntry &&
									!countersignEntry.isReattested &&
									validateTemplateIsCountersignable() &&
									validateUserCanCountersign()
								: true) && (
								<Button
									variant="primary"
									additionalClasses="w-full"
									disabled={pinning || selectedForm.slug.length == 0}
									onClick={() => {
										setPinning(true)
										setErrorMessage('')
										const isReadyToSubmit = validateRequiredFields(true)
										if (isReadyToSubmit) {
											handleSubmit()
										}
									}}
								>
									{pinning ? 'Loading...' : 'Continue'}
								</Button>
							)}
						{isUserAuthenticated() && isDataPinned() && !signData && (
							<>
								<Button
									variant="primary"
									leftIcon={<SignIcon />}
									additionalClasses="w-full"
									disabled={signingMessage.length != 0}
									onClick={() => handlePrepareSign()}
								>
									Sign
								</Button>
								{(!pendingPin || !pendingPin.rollupCID) && (
									<Button
										variant="secondary"
										additionalClasses="w-full mt-2"
										disabled={signingMessage.length != 0}
										onClick={handleBackToEdit}
									>
										Back to edit
									</Button>
								)}
							</>
						)}
					</div>
				</div>
			)}
		</div>
	)
}
